/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import { Alert } from "reactstrap";

/**
 * Shows a live countdown until `targetIso` when that instant is still in the future.
 * When time is up, calls `onExpire` once (e.g. refetch booking details).
 */
export default function SmartMatchingAutoStartCountdown({ targetIso, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  const expiredRef = useRef(false);

  const targetMs = useMemo(() => {
    if (!targetIso) return null;
    const t = new Date(targetIso);
    const ms = t.getTime();
    return Number.isFinite(ms) ? ms : null;
  }, [targetIso]);

  useEffect(() => {
    expiredRef.current = false;
  }, [targetIso]);

  useEffect(() => {
    if (targetMs == null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetMs]);

  useEffect(() => {
    if (targetMs == null || expiredRef.current) return;
    if (Date.now() >= targetMs) {
      expiredRef.current = true;
      onExpire?.();
    }
  }, [targetMs, now, onExpire]);

  if (targetMs == null) return null;
  const remaining = targetMs - now;
  if (remaining <= 0) return null;

  const totalSec = Math.floor(remaining / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;

  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  parts.push(`${s}s`);

  return (
    <Alert color="info" className="mb-4 d-flex align-items-center gap-2">
      <i className="mdi mdi-clock-outline fs-4" aria-hidden />
      <span>
        Smart matching will run automatically in <strong>{parts.join(" ")}</strong>.
      </span>
    </Alert>
  );
}
